import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { FormWithFields } from "@/types/forms";

type Field = FormWithFields["fields"][number];

const typeLabels: Record<string, string> = {
  short_text: "Short Text",
  long_text: "Long Text",
  rich_text: "Rich Text",
  number: "Number",
  slider: "Slider",
  rating: "Rating",
  dropdown: "Dropdown",
  checkbox: "Checkbox",
  yes_no: "Yes/No",
  emoji_scale: "Emoji Scale",
  date: "Date",
  time: "Time",
  datetime: "Date & Time",
};

interface FieldCardProps {
  field: Field;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}

export function FieldCard({ field, index, onEdit, onDelete }: FieldCardProps) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground w-6">
          {index + 1}.
        </span>
        <div>
          <p className="text-sm font-medium">{field.label}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {typeLabels[field.type] ?? field.type}
            </span>
            {field.required && (
              <Badge variant="secondary" className="text-[10px] px-1 py-0">
                required
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete field</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{field.label}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
